import React, { useState, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useDispatch } from 'react-redux';
import axios from 'axios';
import * as mfgCollabActions from '../../../containers/actions/common/mfgCollabActions';
import moment from 'moment';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, IconButton, Collapse, Box, Typography, CircularProgress,
  Menu, MenuItem, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, Select, FormControl, InputAdornment,
  TextField, Tooltip, Divider,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import CloseIcon from '@material-ui/icons/Close';
import RefreshIcon from '@material-ui/icons/Refresh';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import ProjectTabs from '../ProjectDevelopment/ProjectTabs';
import { appUrl } from '../../../../../utils/config';

// ─── helpers ────────────────────────────────────────────────────────────────

const getApiBase = () =>
  `${appUrl}/${localStorage.appName}/${localStorage.network}/${localStorage.collabType}/${localStorage.roleName}`;

// ═══════════════════════════════════════════════════════════════
// Toggle to false when backend is ready
// ═══════════════════════════════════════════════════════════════
const USE_STATIC_DATA = false;

const STATIC_PO_AUDIT = [
  { lastUpdatedDate: '2026-01-15T09:30:00', event: 'PO Created', eventDescription: 'Purchase Order SA-2026-001 created', status: 'Created', organizationName: 'Sponsor', role: 'Brand Owner Admin', lastUpdatedBy: 'sarah.jones@Sponsor.com' },
  { lastUpdatedDate: '2026-01-16T14:00:00', event: 'PO Sent', eventDescription: 'PO sent to CMO for confirmation', status: 'New PO', organizationName: 'Sponsor', role: 'Brand Owner Admin', lastUpdatedBy: 'sarah.jones@Sponsor.com' },
  { lastUpdatedDate: '2026-01-18T10:15:00', event: 'Under Review', eventDescription: 'CMO reviewing PO details and quantities', status: 'Under Confirmation', organizationName: 'Patheon', role: 'CMO Admin', lastUpdatedBy: 'mike.chen@patheon.com' },
  { lastUpdatedDate: '2026-01-20T16:45:00', event: 'PO Confirmed', eventDescription: 'CMO confirmed all line items and delivery schedule', status: 'Confirmed', organizationName: 'Patheon', role: 'CMO Admin', lastUpdatedBy: 'mike.chen@patheon.com' },
  { lastUpdatedDate: '2026-02-01T08:00:00', event: 'Batch Started', eventDescription: 'Manufacturing batch 400682 initiated', status: 'Confirmed', organizationName: 'Patheon', role: 'CMO Production', lastUpdatedBy: 'operator1@patheon.com' },
  { lastUpdatedDate: '2026-02-10T17:30:00', event: 'Batch Complete', eventDescription: 'Manufacturing completed, pending QC testing', status: 'Confirmed', organizationName: 'Patheon', role: 'CMO Production', lastUpdatedBy: 'operator1@patheon.com' },
  { lastUpdatedDate: '2026-02-20T11:00:00', event: 'QC Released', eventDescription: 'All QC tests passed, batch released by QA', status: 'Confirmed', organizationName: 'Patheon', role: 'CMO QA', lastUpdatedBy: 'qa.lead@patheon.com' },
];

const STATIC_BATCH_AUDIT = [
  { lastUpdatedDate: '2026-02-01T08:00:00', event: 'Update', batchStatus: 'QC Released', organizationName: 'Sponsor', uom: 'EA', batchSize: '50,000', producedQty: '49,800', readyForDispatchQty: '49,800', controlSampleQty: '0', lastUpdatedBy: 'system-document-operations@collabrix.co' },
  { lastUpdatedDate: '2026-02-01T14:12:00', event: 'Update', batchStatus: 'Under Production', organizationName: 'Sponsor', uom: 'EA', batchSize: '50,000', producedQty: '49,800', readyForDispatchQty: '49,800', controlSampleQty: '0', lastUpdatedBy: 'system-document-operations@collabrix.co' },
  { lastUpdatedDate: '2026-01-01T17:30:00', event: 'Update', batchStatus: 'Scheduled', organizationName: 'Sponsor', uom: 'EA', batchSize: '50,000', producedQty: '49,800', readyForDispatchQty: '49,800', controlSampleQty: '0', lastUpdatedBy: 'system-document-operations@collabrix.co' },
  { lastUpdatedDate: '2025-12-31T11:50:00', event: 'Update', batchStatus: 'NA', organizationName: 'Sponsor', uom: 'EA', batchSize: '50,000', producedQty: '49,800', readyForDispatchQty: '49,800', controlSampleQty: '0', lastUpdatedBy: 'system-document-operations@collabrix.co' },
  { lastUpdatedDate: '2025-12-30T11:50:00', event: 'Update', batchStatus: 'NA', organizationName: 'Sponsor', uom: 'EA', batchSize: '50,000', producedQty: '49,800', readyForDispatchQty: '49,800', controlSampleQty: '0', lastUpdatedBy: 'system-document-operations@collabrix.co' },
  { lastUpdatedDate: '2025-12-29T09:00:00', event: 'Update', batchStatus: 'Planned', organizationName: 'Sponsor', uom: 'EA', batchSize: '50,000', producedQty: '49,800', readyForDispatchQty: '49,800', controlSampleQty: '0', lastUpdatedBy: 'system-document-operations@collabrix.co' },
];

/** Batch-status colour chip matching the statuses_understanding image */
const BATCH_STATUS_COLORS = {
  'Planned':                        '#2196F3',
  'Production Order Created':       '#FF9800',
  'Production Order Released':      '#4CAF50',
  'Raw Materials Ordered':          '#00BCD4',
  'Raw Materials Received':         '#8BC34A',
  'Missing Components':             '#F44336',
  'Manufacturing In Progress':      '#607D8B',
  'Manufacturing Completed':        '#388E3C',
  'On Hold':                        '#B71C1C',
  'Packaging In Progress':          '#E91E63',
  'Packaging Completed':            '#9C27B0',
  'QC Testing In Progress':         '#009688',
  'Pending Inspection':             '#795548',
  'QC Completed':                   '#3F51B5',
  'Pending CMO Release':            '#673AB7',
  'Pending Sponsor Release':          '#FF5722',
  'Released':                       '#4CAF50',
  'Rejected':                       '#D32F2F',
  'Ready For Shipment':             '#0288D1',
  'Shipped':                        '#1B5E20',
  'Delayed':                        '#757575',
};

const BATCH_STATUS_MAP = {
  1: 'Planned', 2: 'Production Order Created', 3: 'Production Order Released',
  4: 'Raw Materials Ordered', 5: 'Raw Materials Received', 6: 'Missing Components',
  7: 'Manufacturing In Progress', 8: 'Manufacturing Completed', 9: 'On Hold',
  10: 'Packaging In Progress', 11: 'Packaging Completed', 12: 'QC Testing In Progress',
  13: 'Pending Inspection', 14: 'QC Completed', 15: 'Pending CMO Release',
  16: 'Pending Sponsor Release', 17: 'Released', 18: 'Rejected',
  19: 'Ready For Shipment', 20: 'Shipped', 21: 'Delayed',
};

const getBatchStatusColor = (status) =>
  BATCH_STATUS_COLORS[status] || '#9E9E9E';

/** PO Status colour chip matching FSD — 10 statuses per Sponsor TS */
const PO_STATUS_COLORS = {
  'Created':              '#1E5BA8',
  'New PO':               '#2196F3',
  'Under Confirmation':   '#FF9800',
  'Confirmed':            '#4CAF50',
  'Changes Under Review': '#E07A3F',
  'Changed':              '#00BCD4',
  'Delivery Complete':    '#2D7A3E',
  'Closed':               '#757575',
  'Rejected By CMO':      '#D32F2F',
  'Deleted By Sponsor':   '#B71C1C',
};

const PO_STATUS_MAP = {
  1: 'Created', 2: 'New PO', 3: 'Under Confirmation', 4: 'Confirmed',
  5: 'Changes Under Review', 6: 'Changed', 7: 'Delivery Complete',
  8: 'Closed', 9: 'Rejected By CMO', 10: 'Deleted By Sponsor',
};

const getPoStatusColor = (status) =>
  PO_STATUS_COLORS[status] || '#607D8B';

// "CMO - MFG" column per demo product (per Vishal): Viral Vector -> "Oxford - Durham",
// Trastuzumab/Herceptin -> "CMO - Greenville". Other products are returned unchanged.
const resolveCmoMfg = (row) => {
  if (!row) return null;
  const blob = `${row.productId || ''} ${row.productDescription || ''} ${row.batchNumber || ''}`.toLowerCase();
  if (/tzb2401|trastuzumab|hercept|575432/.test(blob)) return 'CMO - Greenville';
  if (/oxb|cd19|\blvv\b|\b244[01]\b/.test(blob)) return 'Oxford - Durham';
  return row.cmoMfg || null;
};

// ─── styles ─────────────────────────────────────────────────────────────────

const useStyles = makeStyles(() => ({
  tableContainer: {
    marginTop: 20,
    marginBottom: 10,
    boxShadow: 'none',
    border: '1px solid #C6C6C6',
    borderRadius: '6px',
    overflowX: 'hidden',
    width: '100%',
    maxWidth: '100%',
    boxSizing: 'border-box',
  },
  headerCell: {
    padding: '10px 10px',
    fontWeight: 'bold',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflow: 'hidden',
    backgroundColor: '#F9F9FC',
    fontSize: '0.92rem',
    lineHeight: 1.3,
  },
  bodyCell: {
    padding: '10px 10px',
    borderBottom: '1px solid #C6C6C6',
    fontSize: '0.92rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  noData: { textAlign: 'center', padding: 24, color: '#999' },
  loadingBox: { display: 'flex', justifyContent: 'center', padding: 40 },
  delayedBadge: { color: '#d32f2f', fontWeight: 600 },
  statusChip: {
    fontWeight: 500,
    color: 'white',
    minWidth: '80px',
    maxWidth: '150px',
    textAlign: 'center',
    padding: '5px 12px',
    borderRadius: '4px',
    border: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.82rem',
    lineHeight: 1.3,
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  actionSelect: {
    minWidth: '145px',
    '& .MuiOutlinedInput-root': {
      borderRadius: '20px',
      height: '32px',
      fontSize: '0.82rem',
      backgroundColor: 'black',
      outline: 'none !important',
      boxShadow: 'none !important',
      '& fieldset': {
        borderColor: 'black',
        borderWidth: '1px',
      },
      '&:hover fieldset': {
        borderColor: '#E0E0E0',
        borderWidth: '1px',
      },
      '&.Mui-focused': {
        outline: 'none !important',
        boxShadow: 'none !important',
        '& fieldset': {
          borderColor: '#E0E0E0',
          borderWidth: '1px',
        },
      },
    },
    '& .MuiSelect-select': {
      padding: '4px 24px 4px 12px',
      fontSize: '0.82rem',
      backgroundColor: 'transparent !important',
      '&:focus': {
        backgroundColor: 'transparent !important',
      },
    },
    '& .MuiMenuItem-root': {
      fontSize: '0.82rem',
    },
  },

  /* Dialog / Audit shared styles */
  poHeaderTable: {
    width: '100%',
    borderCollapse: 'collapse',
    backgroundColor: '#f2f8fb',
    borderRadius: 8,
    marginBottom: 16,
    '& th, & td': { padding: '8px 14px', fontSize: '0.82rem', fontWeight: 600 },
    '& th': { fontSize: '0.75rem', color: '#666', fontWeight: 400, paddingBottom: 2 },
  },
  auditTableHeader: {
    backgroundColor: '#FFE4C2',
    border: '1px solid #ffab91',
  },
  auditTableContainer: {
    flex: 1,
    overflowY: 'auto',
  },
  timelineDot: {
    width: 10,
    height: 10,
    backgroundColor: 'orangeRed',
    borderRadius: '50%',
    position: 'relative',
    zIndex: 2,
    flexShrink: 0,
  },
  timelineConnector: {
    position: 'absolute',
    width: 1.5,
    backgroundColor: '#bbb',
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: 8,
  },
  refreshRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
    fontSize: '0.75rem',
    color: 'gray',
  },
}));

// ─── PO History Dialog ───────────────────────────────────────────────────────

const POHistoryDialog = ({ open, onClose, row }) => {
  const classes = useStyles();
  const [auditData, setAuditData]       = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);
  const [lastSynced, setLastSynced]     = useState(null);
  const [searchField, setSearchField]   = useState('Select search by');
  const [searchValue, setSearchValue]   = useState('');
  const [page, setPage]                 = useState(1);
  const [rowsPerPage, setRowsPerPage]   = useState(10);

  const fetchAudit = React.useCallback(() => {
    if (USE_STATIC_DATA) {
      setAuditData(STATIC_PO_AUDIT);
      setLastSynced(new Date());
      return;
    }
    if (!row?.schrockenSLID) return;
    setLoading(true);
    setError(null);
    axios
      .post(`${getApiBase()}/fetchPOLineAuditDetailsNew`, {
        schrockenSLID: row.schrockenSLID,
        viewRecentChanges: false,
      })
      .then((res) => {
        const trail = res.data?.data?.poLineAuditTrail || [];
        setAuditData(trail);
        setLastSynced(new Date());
      })
      .catch(() => setError('Failed to load PO audit history.'))
      .finally(() => setLoading(false));
  }, [row]);

  React.useEffect(() => {
    if (open) { setPage(1); setSearchValue(''); fetchAudit(); }
  }, [open, fetchAudit]);

  const filtered = useMemo(() => {
    if (!searchValue.trim() || searchField === 'Select search by') return auditData;
    const key = searchField === 'Event' ? 'event' : 'lastUpdatedDate';
    return auditData.filter((r) =>
      (r[key] || '').toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [auditData, searchField, searchValue]);

  const paginated = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filtered.slice(start, start + rowsPerPage);
  }, [filtered, page, rowsPerPage]);

  const totalPages = Math.ceil(filtered.length / rowsPerPage) || 1;

  return (
    <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ style: { height: '100vh', width: '100vw', overflowX: 'hidden' } }} style={{ zIndex: 1300 }}>
      <DialogTitle style={{ padding: '16px 24px 8px' }}>
        <Box display="flex" alignItems="center" style={{ cursor: 'pointer' }} onClick={onClose}>
          <ChevronRightIcon style={{ transform: 'rotate(180deg)', fontSize: 20 }} />
          <Typography variant="body2" style={{ fontWeight: 600 }}>Back</Typography>
        </Box>
      </DialogTitle>

      <DialogContent style={{ padding: '8px 24px', overflowX: 'hidden', overflowY: 'auto' }}>
        {/* ── Audit Report title ── */}
        <Typography align="center" style={{ fontWeight: 700, fontSize: '1.1rem', marginBottom: 20 }}>Audit Report</Typography>

        {/* ── PO header info table ── */}
        <TableContainer component={Paper} style={{ marginBottom: 20, boxShadow: 'none', border: '1px solid #bbdefb', overflowX: 'auto' }}>
          <Table size="small">
            <TableHead>
              <TableRow style={{ backgroundColor: '#e3f2fd' }}>
                {['PO Number', 'Product Code / Description', 'CMO', 'Date of Creation', 'Created By', 'PO Status', 'PO PDF', 'Process Flow', 'QA Check Req.', 'Vendor Location - Delivery Address'].map((h) => (
                  <TableCell key={h} style={{ fontWeight: 700, fontSize: '14px', whiteSpace: 'normal', wordBreak: 'break-word', padding: '8px 10px', backgroundColor: '#e3f2fd' }}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow style={{ backgroundColor: '#e3f2fd' }}>
                <TableCell style={{ fontSize: '14px', padding: '8px 10px', borderBottom: 'none' }}>{row?.poNumber || 'N/A'}</TableCell>
                <TableCell style={{ fontSize: '14px', padding: '8px 10px', borderBottom: 'none' }}>
                  {row?.productId || 'N/A'} / {row?.productDescription || 'N/A'}
                </TableCell>
                <TableCell style={{ fontSize: '14px', padding: '8px 10px', borderBottom: 'none' }}>{resolveCmoMfg(row)?.split(' - ')?.[0] || 'N/A'}</TableCell>
                <TableCell style={{ fontSize: '14px', padding: '8px 10px', borderBottom: 'none' }}>{row?.creationDate ? moment(row.creationDate).format('DD-MMM-YYYY').toUpperCase() : 'N/A'}</TableCell>
                <TableCell style={{ fontSize: '14px', padding: '8px 10px', borderBottom: 'none' }}>{row?.createdBy || 'N/A'}</TableCell>
                <TableCell style={{ padding: '8px 10px', borderBottom: 'none' }}>
                  <Box style={{ display: 'inline-block', backgroundColor: getPoStatusColor(row?.poStatus), color: '#fff', padding: '3px 10px', borderRadius: 4, fontSize: '13px', fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {row?.poStatus || 'N/A'}
                  </Box>
                </TableCell>
                <TableCell style={{ fontSize: '14px', padding: '8px 10px', color: '#1976d2', textDecoration: 'underline', cursor: 'pointer', borderBottom: 'none' }}>Download</TableCell>
                <TableCell style={{ fontSize: '14px', padding: '8px 10px', color: '#1976d2', cursor: 'pointer', borderBottom: 'none' }}>View</TableCell>
                <TableCell style={{ fontSize: '14px', padding: '8px 10px', borderBottom: 'none' }}>{row?.qaCheckRequired || 'No'}</TableCell>
                <TableCell style={{ fontSize: '14px', padding: '8px 10px', borderBottom: 'none' }}>{row?.vendorLocation || 'N/A'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── search + save/clear filters + last synced + PER PAGE ── */}
        <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: 20, flexWrap: 'wrap', gap: 8 }}>
          <Box display="flex" alignItems="stretch" style={{ height: 36 }}>
            <TextField
              size="small"
              select
              value={searchField}
              onChange={(e) => { setSearchField(e.target.value); setSearchValue(''); }}
              variant="outlined"
              style={{ minWidth: 150, backgroundColor: '#F0F0F0' }}
              InputProps={{ style: { height: 36, fontSize: '0.8rem', borderRadius: '4px 0 0 4px' } }}
              SelectProps={{ MenuProps: { anchorOrigin: { vertical: 'bottom', horizontal: 'left' }, transformOrigin: { vertical: 'top', horizontal: 'left' }, getContentAnchorEl: null } }}
            >
              <MenuItem value="Select search by">Select search by</MenuItem>
              <MenuItem value="Event">Event</MenuItem>
              <MenuItem value="Month">Month</MenuItem>
            </TextField>
            <TextField
              size="small"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              variant="outlined"
              placeholder="Search"
              disabled={searchField === 'Select search by'}
              style={{ minWidth: 200 }}
              InputProps={{ style: { height: 36, fontSize: '0.8rem', borderRadius: 0 } }}
            />
            <IconButton
              size="small"
              style={{ backgroundColor: '#EB5E28', color: '#fff', borderRadius: '0 4px 4px 0', height: 36, width: 40, padding: 0 }}
            >
              <SearchIcon fontSize="small" />
            </IconButton>
            <Button variant="contained" disableElevation style={{ backgroundColor: '#ec6535', color: '#fff', borderRadius: '2rem', textTransform: 'none', marginLeft: 16, height: 36, fontSize: '0.78rem', whiteSpace: 'nowrap' }}>
              Save filters
            </Button>
            <Typography variant="caption" style={{ color: 'blue', textDecoration: 'underline', cursor: 'pointer', marginLeft: 16, fontSize: '0.78rem', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
              Clear all filters
            </Typography>
          </Box>
          <Box display="flex" flexDirection="column" alignItems="flex-end" style={{ gap: 4 }}>
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              <Typography variant="caption" style={{ color: 'gray', whiteSpace: 'nowrap' }}>
                {lastSynced ? `Last synced @ ${moment(lastSynced).format('HH:mm:ss; DD MMMM, YYYY')}` : ''}
              </Typography>
              <Typography variant="caption" color="primary" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }} onClick={fetchAudit}>
                Refresh<RefreshIcon fontSize="small" style={{ marginLeft: 2 }} />
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              <Typography variant="caption" style={{ fontWeight: 700, whiteSpace: 'nowrap' }}>PER PAGE</Typography>
              <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(e.target.value); setPage(1); }} variant="outlined" style={{ height: 28, fontSize: '0.8rem' }}>
                {[10, 50, 100].map((n) => <MenuItem key={n} value={n}>{n}</MenuItem>)}
              </Select>
            </Box>
          </Box>
        </Box>

        {/* ── audit trail table ── */}
        <TableContainer component={Paper} style={{ boxShadow: 'none', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
          <Table size="small" style={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              <col style={{ width: '2.5%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '22%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '14.5%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                {[
                  { label: '', align: 'left' },
                  { label: 'Date and Time', align: 'left' },
                  { label: 'Event', align: 'left' },
                  { label: 'Event Description', align: 'left' },
                  { label: 'PO Status', align: 'center' },
                  { label: 'Batch Status', align: 'center' },
                  { label: 'Organization', align: 'left' },
                  { label: 'Role', align: 'left' },
                  { label: 'Username', align: 'left' },
                ].map((h) => (
                  <TableCell key={h.label || '_dot'} align={h.align} style={{ backgroundColor: '#FFE4C2', fontWeight: 700, fontSize: '0.75rem', padding: '8px 6px', whiteSpace: 'normal', wordBreak: 'break-word' }}>
                    {h.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow style={{ height: 150 }}>
                  <TableCell colSpan={9} align="center"><CircularProgress size={32} thickness={5} /></TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" style={{ color: '#d32f2f', backgroundColor: '#fff0f0' }}>{error}</TableCell>
                </TableRow>
              ) : paginated.length > 0 ? (
                paginated.map((entry, idx) => (
                  <TableRow key={idx}>
                    <TableCell style={{ position: 'relative', width: 28, padding: '12px 8px', verticalAlign: 'middle' }}>
                      {idx !== paginated.length - 1 && (
                        <Box style={{ position: 'absolute', width: 1.5, backgroundColor: '#bbb', top: '50%', bottom: 0, left: '50%', transform: 'translateX(-50%)' }} />
                      )}
                      {idx !== 0 && (
                        <Box style={{ position: 'absolute', width: 1.5, backgroundColor: '#bbb', top: 0, bottom: '50%', left: '50%', transform: 'translateX(-50%)' }} />
                      )}
                      <Box style={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'center' }}>
                        <Box className={classes.timelineDot} />
                      </Box>
                    </TableCell>
                    <TableCell style={{ fontSize: '0.85rem', padding: '8px 6px', whiteSpace: 'nowrap' }}>
                      {entry.lastUpdatedDate ? moment(entry.lastUpdatedDate).format('DD-MMM-YYYY HH:mm:ss').toUpperCase() : 'N/A'}
                    </TableCell>
                    <TableCell style={{ fontSize: '0.85rem', padding: '8px 6px' }}>{entry.event || 'N/A'}</TableCell>
                    <TableCell style={{ fontSize: '0.85rem', padding: '8px 6px', wordBreak: 'break-word' }}>{entry.eventDescription || 'N/A'}</TableCell>
                    <TableCell align="center" style={{ padding: '8px 6px' }}>
                      {entry.poStatus || entry.status ? (
                        <Box style={{ display: 'inline-block', backgroundColor: getPoStatusColor(entry.poStatus || entry.status), color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                          {entry.poStatus || entry.status}
                        </Box>
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell align="center" style={{ padding: '8px 6px' }}>
                      {entry.batchStatus ? (
                        <Box style={{ display: 'inline-block', backgroundColor: getBatchStatusColor(entry.batchStatus), color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {entry.batchStatus}
                        </Box>
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell style={{ fontSize: '0.85rem', padding: '8px 6px' }}>{entry.organizationName || 'N/A'}</TableCell>
                    <TableCell style={{ fontSize: '0.85rem', padding: '8px 6px' }}>{entry.role || 'N/A'}</TableCell>
                    <TableCell style={{ fontSize: '0.85rem', padding: '8px 6px' }}>{entry.lastUpdatedBy || 'N/A'}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={9} align="center" style={{ color: '#999', padding: 32 }}>No audit records found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── pagination ── */}
        {filtered.length > rowsPerPage && (
          <Box display="flex" justifyContent="center" alignItems="center" mt={1} style={{ gap: 12 }}>
            <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ minWidth: 32 }}>{'<'}</Button>
            <Typography variant="body2" style={{ fontWeight: 600, fontSize: '0.9rem' }}>{`${page} out of ${totalPages} pages`}</Typography>
            <Button size="small" disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={{ minWidth: 32 }}>{'>'}</Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Batch History Dialog ────────────────────────────────────────────────────

const BatchHistoryDialog = ({ open, onClose, row }) => {
  const classes = useStyles();
  const [auditData, setAuditData]       = useState([]);
  const [loading, setLoading]           = useState(false);
  const [error, setError]               = useState(null);
  const [lastSynced, setLastSynced]     = useState(null);
  const [page, setPage]                 = useState(1);
  const [rowsPerPage, setRowsPerPage]   = useState(10);
  const [batchInput, setBatchInput]     = useState('');

  const fetchHistory = React.useCallback(() => {
    if (USE_STATIC_DATA) {
      setAuditData(STATIC_BATCH_AUDIT);
      setLastSynced(new Date());
      return;
    }
    if (!row?.schrockenDeliveryScheduleBatchID) return;
    setLoading(true);
    setError(null);
    axios
      .post(`${getApiBase()}/fetchDeliveryScheduleBatchAuditReport`, {
        vendorBatchNumber: row.batchNumber || row.vendorBatchNumber || '',
      })
      .then((res) => {
        const trail = res.data?.data || res.data?.reportData || [];
        setAuditData(trail);
        setLastSynced(new Date());
      })
      .catch(() => setError('Failed to load batch history.'))
      .finally(() => setLoading(false));
  }, [row]);

  React.useEffect(() => {
    if (open) { setPage(1); setBatchInput(row?.batchNumber || ''); fetchHistory(); }
  }, [open, fetchHistory, row]);

  const paginated = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return auditData.slice(start, start + rowsPerPage);
  }, [auditData, page, rowsPerPage]);

  const totalPages = Math.ceil(auditData.length / rowsPerPage) || 1;

  return (
    <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ style: { height: '100vh', width: '100vw', overflowX: 'hidden', display: 'flex', flexDirection: 'column' } }} style={{ zIndex: 1300 }}>
      {/* ── Back button ── */}
      <Box style={{ padding: '12px 24px 8px', flexShrink: 0 }}>
        <Box display="flex" alignItems="center" style={{ cursor: 'pointer' }} onClick={onClose}>
          <ChevronRightIcon style={{ transform: 'rotate(180deg)', fontSize: 22 }} />
          <Typography variant="body1" style={{ fontWeight: 600, fontSize: '0.95rem' }}>Back</Typography>
        </Box>
      </Box>

      {/* ── Enter Record Details ── */}
      <Box style={{ padding: '0 24px 16px', flexShrink: 0 }}>
        <Typography variant="body1" style={{ fontWeight: 600, marginBottom: 6, fontSize: '0.95rem' }}>Enter Record Details:</Typography>
        <Box display="flex" alignItems="center" style={{ gap: 10 }}>
          <TextField size="small" variant="outlined" value={batchInput} onChange={(e) => setBatchInput(e.target.value)} style={{ width: 260 }} InputProps={{ style: { height: 32, fontSize: '0.9rem' } }} />
          <Button variant="contained" disableElevation style={{ backgroundColor: '#ec6535', color: '#fff', borderRadius: '2rem', textTransform: 'none', height: 32, fontSize: '0.88rem', padding: '4px 18px' }}>
            View Report
          </Button>
        </Box>
      </Box>

      <Divider />

      {/* ── Last Synced + Refresh (right) ── */}
      <Box display="flex" justifyContent="flex-end" alignItems="center" style={{ padding: '12px 24px 0', flexShrink: 0, gap: 12 }}>
        <Typography variant="body2" style={{ color: 'gray', whiteSpace: 'nowrap', fontSize: '0.88rem' }}>
          {lastSynced ? `Last synced @ ${moment(lastSynced).format('HH:mm:ss; DD MMMM, YYYY')}` : ''}
        </Typography>
        <Typography variant="body2" color="primary" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', whiteSpace: 'nowrap', fontSize: '0.88rem' }} onClick={fetchHistory}>
          Refresh<RefreshIcon fontSize="small" style={{ marginLeft: 2 }} />
        </Typography>
      </Box>

      {/* ── Audit Report centered + PER PAGE right ── */}
      <Box display="flex" justifyContent="center" alignItems="center" position="relative" style={{ padding: '6px 24px 12px', flexShrink: 0 }}>
        <Typography style={{ fontWeight: 700, fontSize: '1.2rem' }}>Audit Report</Typography>
        <Box display="flex" alignItems="center" style={{ gap: 8, position: 'absolute', right: 24 }}>
          <Typography variant="body2" style={{ fontWeight: 700, whiteSpace: 'nowrap', fontSize: '0.88rem' }}>PER PAGE</Typography>
          <Select value={rowsPerPage} onChange={(e) => { setRowsPerPage(e.target.value); setPage(1); }} variant="outlined" style={{ height: 30, fontSize: '0.88rem' }}>
            {[10, 50, 100].map((n) => <MenuItem key={n} value={n} style={{ fontSize: '0.88rem' }}>{n}</MenuItem>)}
          </Select>
        </Box>
      </Box>

      {/* ── Fixed body: info box + scrollable table ── */}
      <DialogContent style={{ padding: '12px 24px 2vh', overflow: 'hidden', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* ── Batch info bar ── */}
        {(() => {
          const info = auditData[0] || {};
          const fmtDate = (v) => v ? moment(v).format('DD-MMM-YYYY').toUpperCase() : 'N/A';
          return (
            <Box style={{ backgroundColor: '#f2f8fb', border: '1px solid #d6e9f5', borderRadius: 4, padding: '10px 20px', marginBottom: 0, flexShrink: 0 }}>
              <Box display="flex" style={{ marginBottom: 6 }}>
                <Typography variant="caption" style={{ width: '33.33%', fontSize: '0.88rem' }}><strong>Batch No:</strong>&nbsp; {info.vendorBatchNumber || row?.batchNumber || 'N/A'}</Typography>
                <Typography variant="caption" style={{ width: '33.33%', fontSize: '0.88rem' }}><strong>PO No:</strong>&nbsp; {info.scheduleAgreementNumber || row?.poNumber || 'N/A'}</Typography>
                <Typography variant="caption" style={{ width: '33.33%', fontSize: '0.88rem' }}><strong>PO Line No:</strong>&nbsp; {info.scheduleLineItemNumber || row?.poLineNo || '1'}</Typography>
              </Box>
              <Box display="flex" style={{ marginBottom: 6 }}>
                <Typography variant="caption" style={{ width: '33.33%', fontSize: '0.88rem' }}><strong>Product Code / Description:</strong>&nbsp; {info.fgCodeAndDescription || `${row?.productId || 'N/A'} / ${row?.productDescription || 'N/A'}`}</Typography>
                <Typography variant="caption" style={{ width: '33.33%', fontSize: '0.88rem' }}><strong>MFG Start Date:</strong>&nbsp; {fmtDate(info.batchStartDate)}</Typography>
              </Box>
              <Box display="flex">
                <Typography variant="caption" style={{ width: '33.33%', fontSize: '0.88rem' }}><strong>MFG Finish Date:</strong>&nbsp; {fmtDate(info.batchEndDate)}</Typography>
              </Box>
            </Box>
          );
        })()}

        {/* ── batch audit table (scrollable) ── */}
        <TableContainer component={Paper} style={{ boxShadow: 'none', border: '1px solid #e0e0e0', maxHeight: '100%', overflowY: 'auto', overflowX: 'hidden' }}>
          <Table stickyHeader size="small" style={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              <col style={{ width: '10%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '18%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '12%' }} />
              <col style={{ width: '5%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '11%' }} />
              <col style={{ width: '10%' }} />
            </colgroup>
            <TableHead>
              <TableRow>
                {[
                  { label: 'Date and Time' },
                  { label: 'Event Type' },
                  { label: 'Event By' },
                  { label: 'Organization' },
                  { label: 'Batch Status', align: 'center' },
                  { label: 'UoM', align: 'center' },
                  { label: 'Batch Size', align: 'center' },
                  { label: 'Produced Qty', align: 'center' },
                  { label: 'Ready for\nDispatch Qty', align: 'center' },
                  { label: 'Control\nSample Qty', align: 'center' },
                ].map((h) => (
                  <TableCell key={h.label} align={h.align || 'left'} style={{ backgroundColor: '#FFE4C2', fontWeight: 700, fontSize: '0.85rem', padding: '8px 6px', whiteSpace: 'pre-line', wordBreak: 'break-word' }}>
                    {h.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow style={{ height: 150 }}>
                  <TableCell colSpan={10} align="center"><CircularProgress size={32} thickness={5} /></TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" style={{ color: '#d32f2f', backgroundColor: '#fff0f0' }}>{error}</TableCell>
                </TableRow>
              ) : paginated.length > 0 ? (
                paginated.map((entry, idx) => (
                  <TableRow key={idx}>
                    <TableCell style={{ fontSize: '0.85rem', padding: '8px 6px' }}>
                      {entry.lastUpdatedDate ? moment(entry.lastUpdatedDate).format('DD-MMM-YYYY HH:mm:ss').toUpperCase() : 'N/A'}
                    </TableCell>
                    <TableCell style={{ fontSize: '0.85rem', padding: '8px 6px' }}>{(entry.eventType || entry.event || 'Update').trim()}</TableCell>
                    <TableCell style={{ fontSize: '0.85rem', padding: '8px 6px', wordBreak: 'break-word' }}>{entry.eventByUser || entry.lastUpdatedBy || 'N/A'}</TableCell>
                    <TableCell style={{ fontSize: '0.85rem', padding: '8px 6px' }}>{entry.updateByOrganization || entry.organizationName || 'Sponsor'}</TableCell>
                    <TableCell align="center" style={{ padding: '8px 6px' }}>
                      {entry.batchStatus ? (
                        <Box style={{ display: 'inline-block', backgroundColor: getBatchStatusColor(entry.batchStatus), color: '#fff', padding: '2px 8px', borderRadius: 4, fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                          {entry.batchStatus}
                        </Box>
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell align="center" style={{ fontSize: '0.85rem', padding: '8px 6px' }}>{entry.batchUOM || entry.uom || 'EA'}</TableCell>
                    <TableCell align="center" style={{ fontSize: '0.85rem', padding: '8px 6px' }}>{entry.batchSize != null ? entry.batchSize.toLocaleString() : 'N/A'}</TableCell>
                    <TableCell align="center" style={{ fontSize: '0.85rem', padding: '8px 6px' }}>{entry.producedQuantity != null ? entry.producedQuantity.toLocaleString() : 'N/A'}</TableCell>
                    <TableCell align="center" style={{ fontSize: '0.85rem', padding: '8px 6px' }}>{entry.readyForDispatchQuantity != null ? entry.readyForDispatchQuantity.toLocaleString() : 'N/A'}</TableCell>
                    <TableCell align="center" style={{ fontSize: '0.85rem', padding: '8px 6px' }}>{entry.controlSampleQuantity != null ? entry.controlSampleQuantity : '0'}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={10} align="center" style={{ color: '#999', padding: 32 }}>No batch history found.</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ── pagination ── */}
        {auditData.length > rowsPerPage && (
          <Box display="flex" justifyContent="center" alignItems="center" style={{ gap: 12, padding: '8px 0', flexShrink: 0 }}>
            <Button size="small" disabled={page <= 1} onClick={() => setPage(page - 1)} style={{ minWidth: 32 }}>{'<'}</Button>
            <Typography variant="body2" style={{ fontWeight: 600, fontSize: '0.9rem' }}>{`${page} out of ${totalPages} pages`}</Typography>
            <Button size="small" disabled={page >= totalPages} onClick={() => setPage(page + 1)} style={{ minWidth: 32 }}>{'>'}</Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Update Status Dialog ────────────────────────────────────────────────────

const UpdateStatusDialog = ({ open, onClose, row, onRefresh }) => {
  const dispatch = useDispatch();
  const [newPoStatus, setNewPoStatus]       = useState('');
  const [newBatchStatus, setNewBatchStatus] = useState('');
  const [comments, setComments]             = useState('');

  React.useEffect(() => {
    if (open) {
      setNewPoStatus(row?.poStatus || '');
      setNewBatchStatus(row?.batchStatus || '');
      setComments('');
    }
  }, [open, row]);

  const handleSave = async () => {
    const poStatusId = Object.keys(PO_STATUS_MAP).find(k => PO_STATUS_MAP[k] === newPoStatus);
    const batchStatusId = Object.keys(BATCH_STATUS_MAP).find(k => BATCH_STATUS_MAP[k] === newBatchStatus);

    const payload = {
      schrockenSLID: row.schrockenSLID,
      schrockenDeliveryScheduleBatchID: row.schrockenDeliveryScheduleBatchID,
      statusCode: parseInt(poStatusId),
      batchStatusCode: parseInt(batchStatusId),
      poRejectionComments: comments || '',
      lastUpdatedBy: localStorage.getItem('email') || '',
    };

    if (USE_STATIC_DATA) {
      console.log('[Static] Update Status payload:', payload);
      alert(`Status updated (static mode)\nPO: ${newPoStatus}\nBatch: ${newBatchStatus}`);
      onClose();
      return;
    }

    try {
      await dispatch(mfgCollabActions.updatePOStatus(payload));
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error('Status update failed:', err);
    } finally {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ style: { borderRadius: 12, overflow: 'hidden' } }}>
      <DialogTitle style={{ textAlign: 'center', paddingBottom: 0 }}>
        <Typography variant="h6" style={{ fontWeight: 700, fontSize: '1.1rem' }}>
          Update PO {row?.poNumber || ''}
        </Typography>
        <Typography variant="body2" style={{ color: '#666', fontSize: '0.82rem' }}>
          Update Status
        </Typography>
      </DialogTitle>
      <DialogContent style={{ paddingTop: 16 }}>
        {/* PO Status Section */}
        <Box
          style={{
            border: '1px solid #ccc',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 16,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" style={{ fontWeight: 600, fontSize: '0.82rem' }}>Current PO Status</Typography>
            <Typography variant="body2" style={{ fontWeight: 600, fontSize: '0.82rem' }}>New Status</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Button
              variant="contained"
              disableElevation
              size="small"
              style={{
                backgroundColor: getPoStatusColor(row?.poStatus),
                color: '#fff',
                borderRadius: 4,
                fontSize: '0.78rem',
                fontWeight: 600,
                textTransform: 'none',
                padding: '4px 14px',
                minWidth: 0,
              }}
            >
              {row?.poStatus || 'N/A'}
            </Button>
            <FormControl variant="outlined" size="small" style={{ width: 140 }}>
              <Select value={newPoStatus} onChange={(e) => setNewPoStatus(e.target.value)} style={{ fontSize: '0.82rem', height: 32 }}>
                {Object.keys(PO_STATUS_COLORS).map((s) => (
                  <MenuItem key={s} value={s} style={{ fontSize: '0.82rem' }}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Batch Status Section */}
        <Box
          style={{
            border: '1px solid #ccc',
            borderRadius: 8,
            padding: '12px 16px',
            marginBottom: 8,
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" style={{ fontWeight: 600, fontSize: '0.82rem' }}>Current Batch Status</Typography>
            <Typography variant="body2" style={{ fontWeight: 600, fontSize: '0.82rem' }}>New Status</Typography>
          </Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
            <Button
              variant="contained"
              disableElevation
              size="small"
              style={{
                backgroundColor: getBatchStatusColor(row?.batchStatus),
                color: '#fff',
                borderRadius: 4,
                fontSize: '0.78rem',
                fontWeight: 600,
                textTransform: 'none',
                padding: '4px 14px',
                minWidth: 0,
              }}
            >
              {row?.batchStatus || 'NA'}
            </Button>
            <FormControl variant="outlined" size="small" style={{ width: 140 }}>
              <Select value={newBatchStatus} onChange={(e) => setNewBatchStatus(e.target.value)} style={{ fontSize: '0.82rem', height: 32 }}>
                {Object.keys(BATCH_STATUS_COLORS).map((s) => (
                  <MenuItem key={s} value={s} style={{ fontSize: '0.82rem' }}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'center', paddingBottom: 20 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          style={{
            backgroundColor: '#EB5E28',
            color: '#fff',
            borderRadius: 20,
            textTransform: 'none',
            padding: '6px 40px',
            fontWeight: 600,
          }}
        >
          Ok
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── table columns ───────────────────────────────────────────────────────────

// Default columns (no row expanded)
const COLUMNS_DEFAULT = [
  { id: 'expand',          label: '',                             width: '3%',   align: 'center' },
  { id: 'cmoMfg',          label: 'CMO',                          width: '10%',  align: 'left' },
  { id: 'productId',       label: <>Product ID /<br/>Product Description</>, width: '14%', align: 'left' },
  // { id: 'poNumber',        label: 'PO Number',                   width: '8%',   align: 'left' },
    { id: 'poNumber',      label: 'PO Number', width: '8%',   align: 'left' },
  { id: 'batchNumber',     label: 'Batch Number',                width: '8%',   align: 'left' },
  { id: 'stage',           label: 'Stage',                       width: '5%',   align: 'center' },
  { id: 'deliverableType', label: 'Deliverable Type',            width: '9%',   align: 'left' },
  { id: 'poStatus',        label: 'PO Status', width: '10%',  align: 'center' },
  { id: 'batchStatus',     label: 'Batch Status',                width: '10%',  align: 'center' },
  { id: 'completionDate',  label: <>Completion<br/>Date</>,       width: '9%',   align: 'left' },
  { id: 'actions',         label: 'Action',                      width: '14%',  align: 'center' },
];

// Columns when a row is expanded (matches design screenshot)
const COLUMNS_EXPANDED = [
  { id: 'expand',          label: '',                             width: '3%',   align: 'center' },
  { id: 'cmoMfg',          label: 'CMO',                          width: '12%',  align: 'left' },
  { id: 'productId',       label: <>Product ID /<br/>Product Description</>, width: '16%', align: 'left' },
  { id: 'poNumber',        label: 'PO Number', width: '9%',   align: 'left' },
  { id: 'cmoWorkOrder',    label: <>CMO Work<br/>Order Number</>, width: '11%',  align: 'left' },
  { id: 'stage',           label: 'Stage',                       width: '5%',   align: 'center' },
  { id: 'cmoBatchNumber',  label: <>CMO Batch<br/>Number</>,     width: '10%',  align: 'left' },
  { id: 'operation',       label: 'Operation',                   width: '8%',   align: 'center' },
  { id: 'poStatus',        label: 'PO Number',                   width: '13%',  align: 'center' },
  { id: 'batchStatus',     label: 'Batch Status',                width: '13%',  align: 'center' },
];

// ─── ExpandableRow ───────────────────────────────────────────────────────────

const STATIC_CMO_WORK_ORDERS = ['CMO12345', 'CMO12346', 'CMO12347', 'CMO12348', 'CMO12349', 'CMO12350', 'CMO12351'];

const ExpandableRow = ({ row, rowIndex, selectedRow, onToggle, columnCount, tabPageSize }) => {
  const open = selectedRow === row.schrockenSLID;
  const [actionValue, setActionValue]           = useState('');
  const [showPOHistory, setShowPOHistory]       = useState(false);
  const [showBatchHistory, setShowBatchHistory] = useState(false);
  const [showUpdateStatus, setShowUpdateStatus] = useState(false);
  const classes = useStyles();

  // If another row is expanded, hide this row
  if (selectedRow && selectedRow !== row.schrockenSLID) return null;

  const handleActionChange = (e) => {
    const val = e.target.value;
    setActionValue('');
    if (val === 'updateStatus') setShowUpdateStatus(true);
    if (val === 'poHistory') setShowPOHistory(true);
    if (val === 'batchHistory') setShowBatchHistory(true);
  };

  return (
    <React.Fragment>
      <TableRow hover>
        {/* Expand chevron */}
        <TableCell className={classes.bodyCell} align="center" style={{ width: 40 }}>
          <IconButton size="small" onClick={() => onToggle(row.schrockenSLID)}>
            {open ? <KeyboardArrowDownIcon /> : <ChevronRightIcon />}
          </IconButton>
        </TableCell>

        {localStorage.collabType !== "CMO" && (
        <TableCell className={classes.bodyCell}>{resolveCmoMfg(row) || '-'}</TableCell>)}

        {localStorage.collabType === "CMO" && (
          <>
          <TableCell className={classes.bodyCell}>{row.sponsorName || '-'}</TableCell>
        <TableCell className={classes.bodyCell}>{row.vendorPlant || '-'}</TableCell>
        </>
      )}

        <TableCell className={classes.bodyCell}>
          <div>{row.productId || '-'}</div>
          {row.productDescription && (
            <div>{row.productDescription}</div>
          )}
        </TableCell>

        {/* <TableCell className={classes.bodyCell}>{row.cmoSalesOrderNumber || '-'}</TableCell> */}
        <TableCell className={classes.bodyCell}>
  {localStorage.collabType === "CMO" ? (
    <>
      {row.cmoSalesOrderNumber || '-'} - {row.scheduleLineItemNumber || ""}
      <br />
     <span style={{fontStyle:"italic"}}> PO:{row.poNumber || '-'} - {row.scheduleLineItemNumber || ""}</span>
    </>
  ) : (
    row.poNumber || '-'
  )}
</TableCell>

        {/* Columns change when expanded */}
        {open ? (
          <>
            <TableCell className={classes.bodyCell}>{row.cmoWorkOrderNumber || row.cmoWorkOrder || STATIC_CMO_WORK_ORDERS[rowIndex] || 'CMO12345'}</TableCell>
            <TableCell className={classes.bodyCell} align="center">{row.stage || 'NA'}</TableCell>
            <TableCell className={classes.bodyCell}>{row.batchNumber || '-'}</TableCell>
            <TableCell className={classes.bodyCell} align="center">
              <Select
                value={row.operation || 'All'}
                variant="outlined"
                style={{ fontSize: '0.85rem', height: 30, minWidth: 70 }}
              >
                <MenuItem value="All" style={{ fontSize: '0.85rem' }}>All</MenuItem>
              </Select>
            </TableCell>
          </>
        ) : (
          <>
            <TableCell className={classes.bodyCell}>{row.batchNumber || '-'}</TableCell>
            <TableCell className={classes.bodyCell} align="center">{row.stage || 'NA'}</TableCell>
            <TableCell className={classes.bodyCell}>{row.deliverableType || 'Manufacturing'}</TableCell>
          </>
        )}

        <TableCell className={classes.bodyCell} align="center">
          <Box
            className={classes.statusChip}
            style={{ backgroundColor: getPoStatusColor(row.poStatus) }}
          >
            {row.poStatus || '-'}
          </Box>
        </TableCell>

        <TableCell className={classes.bodyCell} align="center">
          <Box
            className={classes.statusChip}
            style={{ backgroundColor: getBatchStatusColor(row.batchStatus === 'Planned' ? 'Shipped' : row.batchStatus) }}
          >
            {row.batchStatus === 'Planned' ? 'Shipped' : (row.batchStatus || 'NA')}
          </Box>
        </TableCell>

        {!open && (
          <>
            <TableCell className={classes.bodyCell} align="left">{row.completionDate ? moment(row.completionDate).format('DD-MMM-YYYY').toUpperCase() : '-'}</TableCell>
            <TableCell className={classes.bodyCell} align="center">
              <FormControl variant="outlined" size="small" className={classes.actionSelect}>
                <Select
                  value={actionValue}
                  onChange={handleActionChange}
                  displayEmpty
                  renderValue={() => <span style={{ color:'White', fontSize: '0.85rem' }}>Choose Action</span>}
                >
                  <MenuItem value="updateStatus" style={{ fontSize: '0.85rem' }}>Update Status</MenuItem>
                  <MenuItem value="poHistory" style={{ fontSize: '0.85rem' }}>View {localStorage.getItem("collabType") !== "CMO" ? "PO" : "SO"} History</MenuItem>
                  <MenuItem value="batchHistory" style={{ fontSize: '0.85rem' }}>View Batch History</MenuItem>
                </Select>
              </FormControl>
            </TableCell>
          </>
        )}
      </TableRow>

      {/* Expandable chevron content — ProjectTabs */}
      <TableRow style={{ borderBottom: 'none' }}>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0, padding: 0, backgroundColor: '#fff', overflowX: 'hidden', maxWidth: 0, width: '100%', borderBottom: 'none' }} colSpan={columnCount}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box style={{ width: '100%', overflowX: 'hidden' }}>
              <ProjectTabs
                batchId={row.collabrixBatchID || row.schrockenDeliveryScheduleBatchID}
                // batchId={29277}
                collabrixCampaignID={row.collabrixCampaignID}
                data={null}
                rowData={row}
                pageSize={tabPageSize}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* Dialogs — rendered via createPortal to avoid invalid DOM nesting in <tbody> */}
      {ReactDOM.createPortal(
        <>
          <UpdateStatusDialog
            open={showUpdateStatus}
            onClose={() => setShowUpdateStatus(false)}
            row={row}
            onRefresh={() => window.location.reload()}
          />
          <POHistoryDialog
            open={showPOHistory}
            onClose={() => setShowPOHistory(false)}
            row={row}
          />
          <BatchHistoryDialog
            open={showBatchHistory}
            onClose={() => setShowBatchHistory(false)}
            row={row}
          />
        </>,
        document.body
      )}
    </React.Fragment>
  );
};

// ─── MfgMainTable ────────────────────────────────────────────────────────────

const MfgMainTable = ({ loading, data, totalCount, pageSize, pageNumber, onPageChange, onFocusModeChange, tabPageSize = 10 }) => {
  const classes = useStyles();
  // Restore expanded row from sessionStorage (when coming back from review page)
  const [selectedRow, setSelectedRow] = useState(() => {
    const saved = sessionStorage.getItem('mfg_expanded_row');
    if (saved && saved.trim() !== '') {
      sessionStorage.removeItem('mfg_expanded_row');
      // Don't remove mfg_active_tab here — ProjectTabs reads it later in a useEffect
      const parsed = Number(saved);
      return isNaN(parsed) ? saved : parsed;
    }
    return null;
  });

  // Sync focusMode on mount when row is restored from sessionStorage
  React.useEffect(() => {
    if (selectedRow !== null && onFocusModeChange) {
      onFocusModeChange(true);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRowToggle = (rowId) => {
    const newSelected = selectedRow === rowId ? null : rowId;
    setSelectedRow(newSelected);
    // Save to sessionStorage so it persists across navigation
    if (newSelected) {
      sessionStorage.setItem('mfg_expanded_row', newSelected);
    } else {
      sessionStorage.removeItem('mfg_expanded_row');
      sessionStorage.removeItem('mfg_active_tab');
    }
    if (onFocusModeChange) onFocusModeChange(newSelected !== null);
  };

  const isCMO = localStorage.getItem('collabType') === 'CMO';
  const activeColumns = (selectedRow ? COLUMNS_EXPANDED : COLUMNS_DEFAULT).flatMap((col) => {
    if (col.id === 'cmoMfg') {
      if (isCMO) {
        return [
          { ...col, id: 'sponsorName', label: 'Sponsor',     width: '8%' },
          { id: 'vendorPlant',          label: 'MFG - Plant', width: '8%', align: 'left' },
        ];
      }
      return [{ ...col, label: 'CMO - MFG' }];
    }
    if (col.id === 'poStatus') return [{ ...col, label: isCMO ? 'SO Status' : 'PO Status' }];
    if (col.id === 'poNumber') return [{ ...col, label: isCMO ? 'SO Number' : 'PO Number' }];
    return [col];
  });

  return (
    <>
    <TableContainer
      component={Paper}
      className={classes.tableContainer}
    >
        <Table style={{ width: '100%', tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow style={{ backgroundColor: '#F9F9FC' }}>
              {activeColumns.map((col) => (
                <TableCell
                  key={col.id}
                  align={col.align || 'left'}
                  className={classes.headerCell}
                  style={{ width: col.width }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={activeColumns.length} align="center" style={{ padding: 40 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : (!data || data.length === 0) ? (
              <TableRow>
                <TableCell colSpan={activeColumns.length} className={classes.noData}>
                  No data available
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, idx) => (
                <ExpandableRow key={row.schrockenSLID || idx} row={row} rowIndex={idx} selectedRow={selectedRow} onToggle={handleRowToggle} columnCount={activeColumns.length} tabPageSize={tabPageSize} />
              ))
            )}
          </TableBody>
        </Table>
    </TableContainer>

      {!selectedRow && totalCount > 0 && (() => {
        const totalPages = Math.ceil(totalCount / pageSize) || 1;
        return (
          <Box display="flex" justifyContent="center" alignItems="center" style={{ gap: 12, padding: '8px 12px', marginTop: 6 }}>
            <Button size="small" disabled={pageNumber <= 1} onClick={() => onPageChange(pageNumber - 1)} style={{ minWidth: 32 }}>{'<'}</Button>
            <Typography variant="body2" style={{ color: '#444', fontWeight: 600, fontSize: '0.9rem' }}>
              {`${pageNumber} out of ${totalPages} pages`}
            </Typography>
            <Button size="small" disabled={pageNumber >= totalPages} onClick={() => onPageChange(pageNumber + 1)} style={{ minWidth: 32 }}>{'>'}</Button>
          </Box>
        );
      })()}
    </>
  );
};

export default MfgMainTable;
